import { PlusIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { memo, useCallback } from 'react';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { T } from '../../../translation/src';
import { OrganisationBillingFieldsFragment } from '../../../types/graphqlTypes';
import { BillingFormValues, BillingInfo, BillingInfoFields, BillingInfoSummary, useBillingInfoForm } from '../BillingInfoForm';

export interface BillingInfoModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  info?: BillingInfo | null;
  // After a save, before the overlay closes: for what the caller shows beyond the details themselves
  // (saving can make the customer in Odoo, which changes the organisation's link to it).
  onSaved?: () => void;
}
// The billing details form in an overlay of its own, over whatever asked for it (an order, the subscription).
const BillingInfoModal_: React.FC2<BillingInfoModalProps> = ({ onClose, onSaved: propsOnSaved, open, organisationId, info }) => {
  const onSaved = useCallback(() => {
    propsOnSaved?.();
    onClose();
  }, [onClose, propsOnSaved]);
  const { formik, saving } = useBillingInfoForm({ organisationId, info, onSaved });
  return (
    // No overflow clipping: the country list drops out of the panel.
    <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-lg" noOverflow>
      <Formik<BillingFormValues> {...formik}>
        <Form className="flex w-full flex-col">
          <Modal.Header className="mb-2!" onClose={onClose}>
            <T _str="billing details" swc />
          </Modal.Header>
          <Modal.Body className="flex flex-col gap-4">
            <span className="text-sm text-gray-500">
              <T _str="These details go on your invoices." />
            </span>
            <BillingInfoFields autoFocus />
          </Modal.Body>
          <Modal.Footer className="justify-between">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              <T _str="cancel" swc />
            </Button>
            <Button variant="primary" type="submit" loading={saving}>
              <T _str="save" swc />
            </Button>
          </Modal.Footer>
        </Form>
      </Formik>
    </Modal.Container>
  );
};
export const BillingInfoModal = memo(BillingInfoModal_);

export interface BillingInfoCardProps {
  // From the organisationBilling query of whoever shows the card (it usually needs "complete" itself).
  billing?: OrganisationBillingFieldsFragment | null;
  // Opens a BillingInfoModal. The card's owner renders it, outside any form of its own: a submit in
  // the overlay would otherwise bubble into that form.
  onEdit: () => void;
  // Why details cannot be added right now, shown in place of the hint; the empty slot is then not a button.
  // For the superadmin while no Odoo customer is linked: filling them in would make a new customer in Odoo,
  // where linking the existing one is what is wanted first. A customer adding their own is never held back.
  addDisabledReason?: React.ReactNode;
  // For the superadmin: confirm the details are left blank on purpose (sales then go ahead, free), and take
  // that back. A customer is never offered this.
  onLeaveBlank?: () => void;
  onUndoBlank?: () => void;
  className?: string;
}
// The billing details as a checkout shows them: who the invoice goes to, with a
// button to fill them in or edit them in an overlay. Nothing where billing is
// not set up (no Odoo) or not known (yet).
const BillingInfoCard_: React.FC2<BillingInfoCardProps> = ({ billing, onEdit, addDisabledReason, onLeaveBlank, onUndoBlank, className }) => {
  if (!billing?.enabled) return null;

  // Left blank on purpose: said so, with the way back.
  if (!billing.complete && billing.blank)
    return (
      <div className={classNames('flex flex-col gap-1 rounded-md border border-orange-500 bg-orange-500/10 p-3', className)}>
        <div className="flex items-center justify-between gap-4">
          <span className="text-sm font-semibold uppercase">
            <T _str="billing details" swc />
          </span>
          <span className="flex gap-3 text-sm">
            {!addDisabledReason && (
              <button type="button" onClick={onEdit} className="text-french-violet-600 underline dark:text-french-violet-400">
                <T _str="add" swc />
              </button>
            )}
            {onUndoBlank && (
              <button type="button" onClick={onUndoBlank} className="text-french-violet-600 underline dark:text-french-violet-400">
                <T _str="undo" swc />
              </button>
            )}
          </span>
        </div>
        <span className="text-xs font-medium text-orange-700 dark:text-orange-400">
          <T _str="Left blank on purpose: there is nobody to invoice. Every plan or project given here is free and makes no invoice in Odoo. Add the details to start charging." />
        </span>
      </div>
    );

  // Nothing to show yet: an empty slot to add them, the way a checkout offers "add address".
  if (!billing.complete)
    return (
      // The add slot and the leave-blank link are sibling buttons: a disabled button swallows every click inside
      // it, so the link must not sit within the add button while there is no customer to add to.
      <div
        className={classNames(
          'flex w-full flex-col gap-1 rounded-md border border-dashed border-gray-300 p-3 transition-colors dark:border-gray-600',
          !addDisabledReason && 'hover:border-gray-500 hover:bg-gray-50 dark:hover:border-gray-400 dark:hover:bg-white/5',
          className,
        )}
      >
        <button type="button" onClick={onEdit} disabled={!!addDisabledReason} className={classNames('flex w-full items-center gap-3 text-left', !!addDisabledReason && 'cursor-not-allowed opacity-50')}>
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-white/10">
            <PlusIcon className="h-5 w-5" />
          </span>
          <span className="flex flex-col">
            <span className="text-sm font-semibold">
              <T _str="add billing details" swc />
            </span>
            <span className="text-xs text-gray-500">{addDisabledReason ?? <T _str="Needed before an order can be invoiced." />}</span>
          </span>
        </button>
        {onLeaveBlank && (
          // Lined up under the text, past the icon (h-8 w-8 and gap-3).
          <span className="pl-11 text-xs text-gray-500">
            <T _str="Nothing can be sold from here until they are filled in, or" />{' '}
            <button type="button" onClick={onLeaveBlank} className="cursor-pointer text-french-violet-600 underline dark:text-french-violet-400">
              <T _str="left blank on purpose" />
            </button>
          </span>
        )}
      </div>
    );

  return (
    <div className={classNames('flex flex-col gap-2 rounded-md border border-gray-300 p-3 dark:border-gray-700', className)}>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm font-semibold uppercase">
          <T _str="billing details" swc />
        </span>
        <button type="button" onClick={onEdit} className="text-sm text-french-violet-600 underline dark:text-french-violet-400">
          <T _str="edit" swc />
        </button>
      </div>
      {billing.info && <BillingInfoSummary info={billing.info} />}
    </div>
  );
};
export const BillingInfoCard = memo(BillingInfoCard_);
